import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Plus, Calculator, Upload, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const currentYear = new Date().getFullYear();
const QUARTERS = [1, 2, 3, 4];

const emptyCreateForm = { fiscal_year: currentYear, account_id: '', cost_center_id: '', fund_id: '', allocated_amount: '', notes: '', quarter: '' };

const ANNUAL_HEADERS = ['Fiscal Year', 'Account Code', 'Cost Center Code', 'Fund Code', 'Allocated Amount', 'Notes'];
const QUARTERLY_HEADERS = ['Fiscal Year', 'Quarter', 'Account Code', 'Cost Center Code', 'Fund Code', 'Allocated Amount', 'Notes'];

const BulkUploadModal = ({ accounts, funds, costCenters, onClose, onDone }) => {
  const [variety, setVariety] = useState('annual'); // 'annual' | 'quarterly'
  const [rows, setRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const accountByCode = new Map(accounts.map(a => [String(a.account_code).trim().toUpperCase(), a]));
  const ccByCode = new Map(costCenters.map(cc => [String(cc.code).trim().toUpperCase(), cc]));
  const fundByCode = new Map(funds.map(f => [String(f.code).trim().toUpperCase(), f]));

  const downloadTemplate = () => {
    const headers = variety === 'quarterly' ? QUARTERLY_HEADERS : ANNUAL_HEADERS;
    const example = variety === 'quarterly'
      ? { 'Fiscal Year': currentYear, 'Quarter': 1, 'Account Code': '5103', 'Cost Center Code': '', 'Fund Code': '', 'Allocated Amount': 100000, 'Notes': '' }
      : { 'Fiscal Year': currentYear, 'Account Code': '5103', 'Cost Center Code': '', 'Fund Code': '', 'Allocated Amount': 100000, 'Notes': '' };
    const ws = XLSX.utils.json_to_sheet([example], { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Budgets');
    XLSX.writeFile(wb, `budget-template-${variety}.xlsx`);
  };

  const validateRow = (row) => {
    const errors = [];
    const fiscalYear = Number(row['Fiscal Year']);
    if (!row['Fiscal Year'] || !Number.isInteger(fiscalYear)) errors.push('Fiscal Year missing/invalid');

    let quarter = null;
    if (variety === 'quarterly') {
      quarter = Number(row['Quarter']);
      if (![1, 2, 3, 4].includes(quarter)) errors.push('Quarter must be 1-4');
    }

    const accountCode = String(row['Account Code'] || '').trim().toUpperCase();
    const account = accountByCode.get(accountCode);
    if (!accountCode) errors.push('Account Code missing');
    else if (!account) errors.push(`Unknown Account Code "${row['Account Code']}"`);

    const ccCode = String(row['Cost Center Code'] || '').trim().toUpperCase();
    const costCenter = ccCode ? ccByCode.get(ccCode) : null;
    if (ccCode && !costCenter) errors.push(`Unknown Cost Center Code "${row['Cost Center Code']}"`);

    const fundCode = String(row['Fund Code'] || '').trim().toUpperCase();
    const fund = fundCode ? fundByCode.get(fundCode) : null;
    if (fundCode && !fund) errors.push(`Unknown Fund Code "${row['Fund Code']}"`);

    const allocatedAmount = Number(row['Allocated Amount']);
    if (!row['Allocated Amount'] || !(allocatedAmount > 0)) errors.push('Allocated Amount missing/invalid');

    return {
      raw: row,
      valid: errors.length === 0,
      errors,
      payload: errors.length === 0 ? {
        fiscal_year: fiscalYear,
        quarter,
        account_id: account.id,
        cost_center_id: costCenter ? costCenter.id : null,
        fund_id: fund ? fund.id : null,
        allocated_amount: allocatedAmount,
        notes: row['Notes'] || null,
      } : null,
    };
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResults(null);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (json.length === 0) {
        toast.error('No rows found in the file');
        setRows(null);
        return;
      }
      setRows(json.map(validateRow));
    } catch (err) {
      toast.error('Could not read that file — is it a valid .xlsx?');
      setRows(null);
    }
  };

  const handleImport = async () => {
    const validRows = rows.filter(r => r.valid);
    if (validRows.length === 0) return toast.error('No valid rows to import');
    setImporting(true);
    const outcomes = [];
    for (const r of validRows) {
      try {
        await accountsApi.post('/budgets', r.payload);
        outcomes.push({ ok: true });
      } catch (err) {
        outcomes.push({ ok: false, message: err.response?.data?.message || 'Failed to create' });
      }
    }
    setImporting(false);
    const successCount = outcomes.filter(o => o.ok).length;
    setResults({ successCount, failCount: outcomes.length - successCount, outcomes });
    if (successCount > 0) {
      toast.success(`${successCount} budget(s) imported`);
      onDone();
    }
    if (outcomes.some(o => !o.ok)) {
      toast.error(`${outcomes.length - successCount} row(s) failed`);
    }
  };

  const validCount = rows ? rows.filter(r => r.valid).length : 0;
  const invalidCount = rows ? rows.length - validCount : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold flex items-center gap-2"><Upload size={18} /> Bulk Upload Budgets</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Variety</label>
              <select value={variety} onChange={(e) => { setVariety(e.target.value); setRows(null); setResults(null); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="annual">Annual Budget</option>
                <option value="quarterly">Quarterly Budget</option>
              </select>
            </div>
            <button type="button" onClick={downloadTemplate}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
              <Download size={16} /> Download Template
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Upload filled-in {variety === 'quarterly' ? 'quarterly' : 'annual'} template (.xlsx)
            </label>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFile}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-800 file:text-white file:text-sm hover:file:bg-blue-900" />
          </div>

          {rows && (
            <>
              <div className="flex gap-4 text-sm">
                <span className="text-green-700 font-medium">{validCount} valid</span>
                {invalidCount > 0 && <span className="text-red-600 font-medium">{invalidCount} with errors (won't be imported)</span>}
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Row</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Account Code</th>
                      <th className="text-right px-3 py-2 text-gray-600 font-semibold">Amount</th>
                      <th className="text-left px-3 py-2 text-gray-600 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={`border-t border-gray-100 ${r.valid ? '' : 'bg-red-50'}`}>
                        <td className="px-3 py-1.5 text-gray-500">{i + 2}</td>
                        <td className="px-3 py-1.5 text-gray-700">{r.raw['Account Code']}</td>
                        <td className="px-3 py-1.5 text-right text-gray-700">{r.raw['Allocated Amount']}</td>
                        <td className={`px-3 py-1.5 ${r.valid ? 'text-green-700' : 'text-red-600'}`}>
                          {r.valid ? 'Valid' : r.errors.join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {results && (
            <div className={`rounded-lg p-3 text-sm ${results.failCount === 0 ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'}`}>
              Imported {results.successCount} of {results.successCount + results.failCount} row(s).
              {results.failCount > 0 && ' Check row errors above and retry a fresh file for the failed ones.'}
            </div>
          )}
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
          <button type="button" onClick={handleImport} disabled={!rows || validCount === 0 || importing}
            className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
            {importing ? 'Importing...' : `Import ${validCount} Row(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageBudgets = ({ accounts, funds, costCenters }) => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState('');
  const [fundId, setFundId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [quarterFilter, setQuarterFilter] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [editingBudget, setEditingBudget] = useState(null);
  const [editForm, setEditForm] = useState({ allocated_amount: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fiscalYear) params.fiscal_year = fiscalYear;
      if (fundId) params.fund_id = fundId;
      if (costCenterId) params.cost_center_id = costCenterId;
      if (quarterFilter) params.quarter = quarterFilter;
      const response = await accountsApi.get('/budgets', { params });
      setBudgets(response.data.budgets);
    } catch (err) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBudgets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.post('/budgets', {
        fiscal_year: Number(createForm.fiscal_year),
        account_id: Number(createForm.account_id),
        cost_center_id: createForm.cost_center_id || null,
        fund_id: createForm.fund_id || null,
        allocated_amount: Number(createForm.allocated_amount),
        notes: createForm.notes || null,
        quarter: createForm.quarter || null,
      });
      toast.success('Budget created');
      setShowCreateModal(false);
      setCreateForm(emptyCreateForm);
      fetchBudgets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create budget');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (budget) => {
    setEditingBudget(budget);
    setEditForm({ allocated_amount: budget.allocated_amount, notes: budget.notes || '' });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await accountsApi.put(`/budgets/${editingBudget.id}`, {
        allocated_amount: Number(editForm.allocated_amount),
        notes: editForm.notes || null,
      });
      toast.success('Budget updated');
      setEditingBudget(null);
      fetchBudgets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update budget');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fiscal Year</label>
              <input type="number" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} placeholder="All years"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-28" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
              <select value={fundId} onChange={(e) => setFundId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Funds</option>
                {funds.map(f => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center</label>
              <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Cost Centers</option>
                {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quarter</label>
              <select value={quarterFilter} onChange={(e) => setQuarterFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All (Annual + Quarterly)</option>
                {QUARTERS.map(q => <option key={q} value={q}>Q{q}</option>)}
              </select>
            </div>
            <button onClick={fetchBudgets} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
              Apply
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 border border-blue-800 text-blue-800 px-4 py-2 rounded-lg text-sm hover:bg-blue-50">
              <Upload size={16} /> Bulk Upload
            </button>
            <button onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
              <Plus size={16} /> New Budget
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Fiscal Year</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Quarter</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Account</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Cost Center</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Fund</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Allocated</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Notes</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : budgets.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">No budgets found</td></tr>
            ) : budgets.map(b => (
              <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-700">{b.fiscal_year}</td>
                <td className="px-4 py-3 text-gray-600">{b.quarter ? `Q${b.quarter}` : 'Annual'}</td>
                <td className="px-4 py-3 text-gray-700">{b.account_code} — {b.account_name}</td>
                <td className="px-4 py-3 text-gray-600">{b.cost_center_code ? `${b.cost_center_code} — ${b.cost_center_name}` : '—'}</td>
                <td className="px-4 py-3 text-gray-600">{b.fund_code ? `${b.fund_code} — ${b.fund_name}` : '—'}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-800">{Number(b.allocated_amount).toFixed(2)}</td>
                <td className="px-4 py-3 text-gray-500">{b.notes || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => openEdit(b)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2"><Calculator size={18} /> New Budget</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fiscal Year</label>
                  <input required type="number" value={createForm.fiscal_year}
                    onChange={(e) => setCreateForm({ ...createForm, fiscal_year: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Quarter (optional)</label>
                  <select value={createForm.quarter} onChange={(e) => setCreateForm({ ...createForm, quarter: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Annual (all year)</option>
                    {QUARTERS.map(q => <option key={q} value={q}>Q{q}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Account</label>
                <select required value={createForm.account_id} onChange={(e) => setCreateForm({ ...createForm, account_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select account...</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center (optional)</label>
                  <select value={createForm.cost_center_id} onChange={(e) => setCreateForm({ ...createForm, cost_center_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">None</option>
                    {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fund (optional)</label>
                  <select value={createForm.fund_id} onChange={(e) => setCreateForm({ ...createForm, fund_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">None</option>
                    {funds.map(f => <option key={f.id} value={f.id}>{f.code}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Allocated Amount</label>
                <input required type="number" step="0.01" min="0" value={createForm.allocated_amount}
                  onChange={(e) => setCreateForm({ ...createForm, allocated_amount: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input type="text" value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingBudget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2"><Calculator size={18} /> Edit Budget</h2>
              <button onClick={() => setEditingBudget(null)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-3">
              <p className="text-xs text-gray-500">
                {editingBudget.fiscal_year} — {editingBudget.account_code} — {editingBudget.account_name}
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Allocated Amount</label>
                <input required type="number" step="0.01" min="0" value={editForm.allocated_amount}
                  onChange={(e) => setEditForm({ ...editForm, allocated_amount: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input type="text" value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditingBudget(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && (
        <BulkUploadModal
          accounts={accounts} funds={funds} costCenters={costCenters}
          onClose={() => setShowBulkModal(false)}
          onDone={fetchBudgets}
        />
      )}
    </>
  );
};

const BudgetVsActual = ({ funds, costCenters }) => {
  const [fiscalYear, setFiscalYear] = useState(String(currentYear));
  const [fundId, setFundId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [quarter, setQuarter] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);

  const ccMap = Object.fromEntries(costCenters.map(cc => [String(cc.id), `${cc.code} — ${cc.name}`]));
  const fundMap = Object.fromEntries(funds.map(f => [String(f.id), `${f.code} — ${f.name}`]));

  const runReport = async () => {
    if (!fiscalYear) return toast.error('Fiscal year is required');
    try {
      setLoading(true);
      const params = { fiscal_year: fiscalYear };
      if (fundId) params.fund_id = fundId;
      if (costCenterId) params.cost_center_id = costCenterId;
      if (quarter) params.quarter = quarter;
      const response = await accountsApi.get('/budget-vs-actual', { params });
      setRows(response.data.budget_vs_actual);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load budget vs actual');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = rows?.reduce((acc, r) => ({
    allocated: acc.allocated + Number(r.allocated_amount),
    actual: acc.actual + Number(r.actual_amount),
  }), { allocated: 0, actual: 0 });

  return (
    <>
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fiscal Year</label>
            <input required type="number" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-28" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
            <select value={fundId} onChange={(e) => setFundId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center</label>
            <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Cost Centers</option>
              {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Quarter</label>
            <select value={quarter} onChange={(e) => setQuarter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Whole Year</option>
              {QUARTERS.map(q => <option key={q} value={q}>Q{q}</option>)}
            </select>
          </div>
          <button onClick={runReport} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            Apply
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Account</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Cost Center</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Fund</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Budget</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Actual Expenditure</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Remaining Balance to Utilize</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Variance %</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : !rows || rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">No budgets found for this fiscal year</td></tr>
            ) : rows.map((r, i) => {
              const budget = Number(r.allocated_amount);
              const actual = Number(r.actual_amount);
              const remaining = budget - actual;
              const variancePct = budget > 0 ? ((actual - budget) / budget) * 100 : 0;
              return (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700">{r.account_code} — {r.account_name}</td>
                  <td className="px-4 py-2 text-gray-600">{ccMap[String(r.cost_center_id)] || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{fundMap[String(r.fund_id)] || '—'}</td>
                  <td className="px-4 py-2 text-right text-gray-800">{budget.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right text-gray-800">{actual.toFixed(2)}</td>
                  <td className={`px-4 py-2 text-right font-medium ${remaining < 0 ? 'text-red-600' : 'text-gray-800'}`}>{remaining.toFixed(2)}</td>
                  <td className={`px-4 py-2 text-right ${variancePct > 0 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{variancePct > 0 ? '+' : ''}{variancePct.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
          {rows && rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-gray-200 font-semibold bg-gray-50">
                <td colSpan={3} className="px-4 py-3 text-right text-gray-600 text-xs">Total</td>
                <td className="px-4 py-3 text-right text-gray-800">{totals.allocated.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-800">{totals.actual.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-800">{(totals.allocated - totals.actual).toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
};

const Budgets = () => {
  const [tab, setTab] = useState('manage'); // 'manage' | 'variance'
  const [accounts, setAccounts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [accRes, fundsRes, ccRes] = await Promise.all([
          accountsApi.get('/chart-of-accounts'),
          accountsApi.get('/funds'),
          accountsApi.get('/cost-centers', { params: { is_active: true } }),
        ]);
        setAccounts(accRes.data.accounts.filter(a => a.is_active));
        setFunds(fundsRes.data.funds);
        setCostCenters(ccRes.data.cost_centers);
      } catch (err) {
        toast.error('Failed to load filter options');
      }
    };
    loadRefs();
  }, []);

  return (
    <Layout title="Budgets">
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button onClick={() => setTab('manage')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'manage' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Manage Budgets
        </button>
        <button onClick={() => setTab('variance')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'variance' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Budget vs Actual
        </button>
      </div>

      {tab === 'manage'
        ? <ManageBudgets accounts={accounts} funds={funds} costCenters={costCenters} />
        : <BudgetVsActual funds={funds} costCenters={costCenters} />}
    </Layout>
  );
};

export default Budgets;
